import { Icon, Tone } from '~/components/ui';
import type { ThreadMessage } from '../api/threadsClient';
import { useLocale, useT } from '~/i18n';
import { absoluteTime, relativeTime } from '../lib/threads';

interface Props {
  message: ThreadMessage;
  /** true mientras el POST está en vuelo: la burbuja ya está, el envío no. */
  pending?: boolean;
}

/**
 * Un mensaje del hilo. Lo propio a la derecha, lo de la empresa a la izquierda.
 *
 * DOS decisiones que no son de estilo:
 *
 * 1. Se pinta `body_text` y NUNCA `body_html`. El HTML de un inbound lo escribió
 *    un desconocido; meterlo con `dangerouslySetInnerHTML` en una pantalla que
 *    lleva la sesión del candidato es una inyección con invitación. El texto
 *    plano llega completo desde el webhook, así que no se pierde nada.
 * 2. El primer mensaje —el sintetizado, que no existe como fila— se rotula
 *    "Postulación enviada" y dice que el CV iba adjunto. Es la única burbuja que
 *    el candidato no escribió a mano y tiene que leerse como lo que es.
 */
export default function MessageBubble({ message, pending }: Props) {
  const t = useT('workspace').bubble;
  const locale = useLocale();
  const outbound = message.direction === 'outbound';
  // El detalle devuelve los `read_at` como estaban ANTES de abrir, justamente
  // para poder marcar acá lo que llegó nuevo en esta visita.
  const isNew = !outbound && !message.read_at;

  const who = message.synthesized ? t.application : outbound ? t.you : t.company;

  return (
    <li className={`msg ${outbound ? 'msg--out' : 'msg--in'}`}>
      <div className="msg__bubble">
        <div className="msg__head">
          <span className="brand-label">{who}</span>
          {isNew && <Tone tone="warn">{t.new}</Tone>}
          {pending && <span className="loading loading-spinner loading-xs" aria-label={t.sendingLabel} />}
        </div>

        {!outbound && message.subject && <p className="msg__subject">{message.subject}</p>}

        <p className="msg__body">{message.body_text}</p>

        {message.synthesized && (
          <p className="msg__attach hush">
            <Icon name="doc" size={14} />
            {t.cvAttached}
          </p>
        )}

        <div className="msg__foot">
          <time dateTime={message.created_at} title={absoluteTime(message.created_at, locale)}>
            {pending ? t.sending : relativeTime(message.created_at, undefined, locale)}
          </time>
          {!outbound && message.from_address && <span>· {message.from_address}</span>}
        </div>
      </div>
    </li>
  );
}
